import { For, Show, createEffect, createMemo, createSignal, on, onCleanup, onMount, untrack } from "solid-js";
import type { JSX } from "solid-js";
import { create } from "@bufbuild/protobuf";
import type { Action } from "../pb/mj/record/v1/record_pb";
import { ActionSchema, ActionType } from "../pb/mj/record/v1/record_pb";
import { doraOf, isTile, tileWords } from "../game/tiles";
import { waits } from "../game/waits";
import { PromptKind } from "../pb/mj/protocol/v1/protocol_pb";
import type { View } from "../game/view";
import { SEATS, dealing, unseen } from "../game/view";
import { H, place } from "../game/place";
import { dice } from "../game/wall";
import type { Geometry, Placement } from "../game/place";
import { boardView } from "../game/auto";
import type { BoardView, Feel } from "../game/auto";
import { t } from "../strings";
import { avatarOfName } from "../game/players";
import type { PlayerInfos } from "../game/players";
import { AVATARS, Avatar } from "./Avatar";
import { Flair, ratingText, Rank } from "./Flair";
import { Controls, OpenControls } from "./Controls";
import { Tile } from "./Tile";
import { TileLayer } from "./TileLayer";
import { calloutSpot, type Box } from "./calloutSpot";
import { milestone, tilesDrawn } from "./milestones";
import { artVersion, setAngle, setSizes, sheetsSettled, tableAngle } from "./tileArt";
import type { OwnTile } from "./TileLayer";
import type { TileMark } from "./Review";
import { promptClock, secondsLeft } from "../game/clock";
import { Countdown, OwnClock } from "./Countdown";
import { weaveSoon } from "./felt";
import { Icon } from "./Icon";

// The table, phone first (design 7 §6.1): the own hand along the bottom; the four rivers around the
// centre, each turned to face its seat as at a physical table; opponents' melds and concealed tiles at
// the edges of their zones.
//
// The page lays out only what stands still: the seats' labels, the centre panel, and empty places for each
// river and hand. Every tile is drawn by the tile layer (design 10 §3), placed at coordinates measured from
// those places, so a move at the table lays out and repaints nothing.

const relative = (v: View, seat: number) => (seat - v.seat + SEATS) % SEATS; // 0 self, 1 right, 2 across, 3 left
const FPS = 60; // the motion's frame budget (design 10 T10: 60, the owner's phone no longer stuttering)
const DIE = ["⚀", "⚁", "⚂", "⚃", "⚄", "⚅"];
// The compass's kanji only ever in their own face (8 KB): laid out while it was still loading, they fell to the
// platform's Mincho, whose first use took a slowed phone's table to 100 ms (Chrome on Windows, 2026-11-03; a Linux
// browser with no such font never shows it). A browser that has had it asks at once, from its cache; on a first visit
// it is asked for once every tile is drawn, as fetched with the tiles it put them 100 ms over a first visit's 2 s
// (design 11 §9). The compass's words wait for it.
const FACE = "700 1em 'Mahhua Display'";
const FACE_SEEN = "mj-display-ja";
let face: Promise<void> | null = null;
function loadFace(): Promise<void> {
  face ??= (globalThis.document?.fonts?.load(FACE, "東") ?? Promise.resolve()).then(
    () => {
      try {
        localStorage.setItem(FACE_SEEN, "1");
      } catch {
        // no storage: asked for after the tiles again next time
      }
    },
    () => undefined,
  );
  return face;
}
try {
  if (localStorage.getItem(FACE_SEEN)) void loadFace();
} catch {
  // no storage: a first visit each time
}

/** The system's reduced motion selects the instant feel unless the player chose one (design 10 §11). */
function effectiveFeel(feel: Feel | undefined): Feel {
  const reduced = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  return feel && feel !== "weighty" ? feel : reduced ? "instant" : "weighty";
}

/** A seat's card (design 14 §7): at its corner of the board, between the rivers, and upright at any lean. */
function PlayerCard(props: {
  v: View;
  seat: number;
  at: (rel: number) => { x: number; y: number; sx: number; sy: number; avatar: number } | null;
  info?: PlayerInfos;
  ratings?: boolean;
  held?: boolean;
}) {
  const rel = () => relative(props.v, props.seat);
  const s = () => props.v.seats[props.seat]!;
  const p = () => props.v.players[props.seat];
  const conn = () => props.v.connections[props.seat];
  const turn = () => props.v.clock?.seat === props.seat;
  const info = () => (p() ? props.info?.[p()!.id] : undefined);
  const avatar = () => info()?.avatar ?? avatarOfName(p()?.name ?? "", AVATARS);
  // Beside the points, and in the bot tag's place: it was last on the line, and read "30+6", "9+" and "29+" (UX review 2026-11-02).
  const clock = () => (turn() && props.seat !== props.v.seat && !props.v.untimed ? props.v.clock : null);
  return (
    <Show when={props.at(rel())}>
      {(q) => (
        <div
          class={`player-card${rel() === 0 ? " own-card" : ""}`}
          style={{ left: `${q().x}px`, top: `${q().y}px`, transform: `translate(${q().sx < 0 ? "-100%" : "0"}, ${q().sy < 0 ? "-100%" : "0"})` }}
        >
          <Avatar id={avatar()} size={q().avatar} />
          <span class="card-lines">
            <span class="card-name">
              <span class="name">{p() ? p()!.name || t.deletedPlayer : "…"}</span>
              <Flair f={info()?.flair} />
            </span>
            <span class="card-sub">
              {/* A new clock (its receivedAt) is a new countdown. */}
              <Show when={clock()} keyed>
                {(c) => <Countdown class="clock" text={(now) => secondsLeft(c.baseMsLeft, c.bankMsLeft, c.receivedAt, now)} />}
              </Show>
              <Show when={props.ratings && info()?.placed}>
                <span class="card-rating" title={info()!.established ? undefined : t.provisionalHint(info()!.provisional_games)}>
                  <Rank x100={info()!.rating_x100} short /> {ratingText(info()!.rating_x100, !info()!.established)}
                </span>
              </Show>
              <Show when={p()?.bot && !clock()}>
                <span class="tag">{t.bot}</span>
              </Show>
              <Show when={s().dead}>
                <span class="tag warn">{t.deadHand}</span>
              </Show>
              <Show when={conn() && !conn()!.connected}>
                <span class="tag warn">{t.away}</span>
              </Show>
              <Show when={conn()?.auto}>
                <span class="tag warn">{t.auto}</span>
              </Show>
            </span>
          </span>
        </div>
      )}
    </Show>
  );
}

/** Each seat's discards, in order, and calls, for a screen reader: the pictures are one layer in no order, with nothing to say whose tile is whose. */
function SeatsInWords(props: { v: View }) {
  const who = (seat: number) => (seat === props.v.seat ? t.you : props.v.players[seat]?.name || t.deletedPlayer);
  return (
    <div class="sr-only">
      <For each={[0, 1, 2, 3]}>
        {(seat) => (
          <Show when={props.v.seats[seat]}>
            {(s) => (
              <section aria-label={`${who(seat)}, ${s().points}`}>
                <p>{seat === props.v.seat ? t.say.yourDiscards : t.say.discardsOf(who(seat))}: {s().river.filter((r) => !r.calledAway).map((r) => tileWords(r.tile, props.v.redFives)).join(", ") || t.say.none}</p>
                <Show when={s().melds.length > 0}>
                  <p>{seat === props.v.seat ? t.say.yourMelds : t.say.meldsOf(who(seat))}:{s().melds.map((m) => m.tiles.map((x) => tileWords(x, props.v.redFives)).join(" ")).join(", ")}</p>
                </Show>
              </section>
            )}
          </Show>
        )}
      </For>
    </div>
  );
}

/** A seat's place on the board: the empty places its river and hand are drawn into. */
function Zone(props: { v: View; seat: number; refs: ZoneRefs }) {
  const rel = () => relative(props.v, props.seat);
  return (
    <div class={`zone zone-${rel()}${props.v.clock?.seat === props.seat ? " turn" : ""}`} ref={(el) => void (props.refs.zone[rel()] = el)}>
      <span class="seat-gap" />
      <div class="river-slot" ref={(el) => void (props.refs.river[rel()] = el)}>
        <span class="probe tile tile-s" />
      </div>
      <div class="wall-slot" ref={(el) => void (props.refs.wall[rel()] = el)}>
        <span class="probe wall-probe" />
      </div>
      <Show when={rel() !== 0}>
        <div class="opponent-hand" ref={(el) => void (props.refs.hand[rel()] = el)}>
          <span class="probe tile tile-s" />
        </div>
      </Show>
    </div>
  );
}

interface ZoneRefs {
  zone: (HTMLDivElement | null)[];
  river: (HTMLDivElement | null)[];
  wall: (HTMLDivElement | null)[];
  hand: (HTMLDivElement | null)[];
}

// A call, riichi or win, announced for a moment on that seat's side of the table; it never blocks a tap. Each
// callout is a new element, so its animation plays again. It stands over the seat's wall, above the tiles
// (audit 2026-10-14: inside the leaning felt it was drawn under the river and over the tile it announced).
// The word itself, as the table calls it (design 15 §1.1): the kana large, the English beneath, no box; a call
// in white, riichi in the accent, a win in vermilion. A screen reader hears the English alone.
const CALL_KIND: Record<string, string> = { DECLARE_RIICHI: "riichi", RON: "win", TSUMO: "win" };
function Callout(props: { v: View; at: (rel: number) => { x: number; y: number; ux: number; uy: number } | null; avoid: () => Box[] }) {
  return (
    <Show when={props.v.callout} keyed>
      {(c) => {
        const p = () => props.at(relative(props.v, c.seat)); // follows the measured places
        return (
          <Show when={p()}>
            {(q) => (
              <div
                class={`callout ${CALL_KIND[c.action] ?? "call"}${t.callsKana[c.action] ? "" : " only-word"}`}
                style={{ left: `${q().x}px`, top: `${q().y}px` }}
                aria-live="polite"
                ref={(el) => {
                  // Never over a river, a call, your hand, a seat's card, the scores or a button (design 15 §11), and on the screen: over its seat's wall where
                  // that is clear, else the nearest place that is. The halo may lie over them; the word may not.
                  // Its size without its snap's scale, its centre (which the scale keeps) for where the layer is.
                  createEffect(() => {
                    const at = q();
                    el.style.left = `${at.x}px`;
                    el.style.top = `${at.y}px`;
                    const lines = [...el.children] as HTMLElement[];
                    const w = Math.max(...lines.map((k) => k.offsetWidth)) + 8;
                    const h = lines.reduce((n, k) => n + k.offsetHeight, 0) + 8;
                    const r = el.getBoundingClientRect();
                    const ox = r.left + r.width / 2 - at.x, oy = r.top + r.height / 2 - at.y; // the layer's origin on the screen
                    // The screen, less what any box around it clips (the board's own edge, on a phone on its side).
                    let l = 8, t = 8, rr = innerWidth - 8, b = innerHeight - 8;
                    for (let a = el.parentElement; a; a = a.parentElement) {
                      if (getComputedStyle(a).overflow === "visible") continue;
                      const k = a.getBoundingClientRect();
                      l = Math.max(l, k.left); t = Math.max(t, k.top); rr = Math.min(rr, k.right); b = Math.min(b, k.bottom);
                    }
                    const room = { l: l - ox, t: t - oy, r: rr - ox, b: b - oy };
                    // The seats' cards, the compass and your buttons too, where the page has laid them out.
                    const cards = [...(el.closest(".table")?.querySelectorAll(".player-card, .center, .controls button") ?? [])].map((k) => {
                      const c = k.getBoundingClientRect();
                      return { l: c.left - ox, t: c.top - oy, r: c.right - ox, b: c.bottom - oy };
                    });
                    const spot = calloutSpot(at, w, h, [...props.avoid(), ...cards], room);
                    if (spot) {
                      el.style.left = `${spot.x}px`;
                      el.style.top = `${spot.y}px`;
                    }
                  });
                }}
              >
                <Show when={t.callsKana[c.action]}>
                  {(kana) => <span class="callout-kana" aria-hidden="true">{kana()}</span>}
                </Show>
                <span class="callout-word">{t.actions[c.action] ?? c.action}</span>
              </div>
            )}
          </Show>
        );
      }}
    </Show>
  );
}

/** A seat's points: a change runs up or down to its new value, a few steps a second, rather than jumping. */
function Points(props: { class: string; value: number }) {
  const [shown, setShown] = createSignal(props.value);
  createEffect(
    on(
      () => props.value,
      (to) => {
        const from = untrack(shown);
        if (from === to) return;
        const start = performance.now();
        const id = setInterval(() => {
          const k = Math.min(1, (performance.now() - start) / 600);
          setShown(k === 1 ? to : Math.round((from + (to - from) * k) / 100) * 100);
          if (k === 1) clearInterval(id);
        }, 66);
        onCleanup(() => clearInterval(id));
      },
      { defer: true },
    ),
  );
  // 25000, as the riichi clients write a score in the compass: a comma is a character the side has no room for.
  return <span class={props.class}>{shown()}</span>;
}

const heldSticks = (v: View, sticks: number, held?: boolean) => (held ? (v.result?.sticks ?? sticks) : sticks);

function Center(props: { v: View; held?: boolean }) {
  // The centre is a compass (design 15 §1.1, UI review V1), as on every riichi table: on each side, the seat's wind and score facing
  // that seat, the dealer's wind in the accent, and at the edge, nearest the seat, its riichi stick; the side of the seat whose turn
  // it is lit, as the turn light on an automatic table (each light fading on a layer of its own). In the corners the sides leave
  // empty, the honba, the riichi sticks on the table and the tiles left. In the middle, standing up to the camera: the round as 東1局
  // with its English beneath, the dice at a hand's start, and the dora.
  const turn = () => (props.v.clock ? relative(props.v, props.v.clock.seat) : -1);
  const honba = (h: { honba: number }) => (props.held ? (props.v.result?.honba ?? h.honba) : h.honba);
  return (
    <Show when={props.v.hand} fallback={<div class="center" />}>
      {(h) => (
        <div class="center compass">
          <For each={[0, 1, 2, 3]}>{(r) => <span class={`turn-light turn-light-${r}${turn() === r ? " on" : ""}`} aria-hidden="true" />}</For>
          <For each={[0, 1, 2, 3]}>
            {(r) => {
              const seat = () => (props.v.seat + r) % SEATS;
              const dealer = () => h().dealer === seat();
              const wind = () => (seat() - h().dealer + SEATS) % SEATS;
              const points = () => (props.held ? (props.v.result?.pointsBefore[seat()] ?? props.v.seats[seat()]?.points ?? 0) : (props.v.seats[seat()]?.points ?? 0));
              return (
                <div class={`compass-side compass-side-${r}`}>
                  <span class="compass-line">
                    <span class={`compass-wind${dealer() ? " dealer" : ""}`} lang="ja" title={t.rounds[wind()]}>
                      {t.windsKanji[wind()]}
                      <span class="sr-only">
                        {" "}
                        {t.rounds[wind()]}
                        {dealer() ? `, ${t.dealer.toLowerCase()}` : ""}
                      </span>
                    </span>
                    <Points class="compass-points" value={points()} />
                  </span>
                  {/* A riichi's thousand-point stick, laid across the seat's side at its edge, slid out from the seat as the riichi is accepted
                      (design 10 §7). Its place is kept without one, so a riichi never moves the score. */}
                  <span class="stick-slot">
                    <Show when={props.v.seats[seat()]?.riichi}>
                      <span class="riichi-stick" aria-label={t.actions.DECLARE_RIICHI} />
                    </Show>
                  </span>
                </div>
              );
            }}
          </For>
          {/* The sticks as sticks and the wall as a wall, each with its count (UI review V1): no words to wrap. */}
          <span class="corner corner-honba" title={`${honba(h())} ${t.honba}`}>
            <span class="stick stick-100" aria-hidden="true" />
            {honba(h())}
            <span class="sr-only"> {t.honba}</span>
          </span>
          <span class="corner corner-sticks" title={`${heldSticks(props.v, h().sticks, props.held)} ${t.sticks(heldSticks(props.v, h().sticks, props.held))}`}>
            <span class="stick stick-1000" aria-hidden="true" />
            {heldSticks(props.v, h().sticks, props.held)}
            <span class="sr-only"> {t.sticks(heldSticks(props.v, h().sticks, props.held))}</span>
          </span>
          <span class="corner corner-wall" title={`${h().wallLeft} ${t.wallLeft}`}>
            <Icon name="wall" />
            <span class="wall-left">{h().wallLeft}</span>
            <span class="sr-only"> {t.wallLeft}</span>
          </span>
          {/* The panel's words as one block, stood up to the camera as one (styles.css). */}
          <div class="center-info">
            <div class="round">
              <span lang="ja" aria-hidden="true">
                {t.roundKanji(h().round, h().handInRound + 1)}
              </span>
              <span class="sr-only">
                {t.rounds[h().round]} {h().handInRound + 1}
              </span>
            </div>
            <div class="small round-en" aria-hidden="true">
              {t.rounds[h().round]} {h().handInRound + 1}
            </div>
            {/* East's dice at the hand's start: they chose where the wall broke (design 10 §4). */}
            <Show when={props.v.lastSeq < 8 ? h().index : null} keyed>
              {(index) => (
                <div class="dice" aria-label={t.dice(dice(props.v.gameId, index))}>
                  <For each={dice(props.v.gameId, index)}>{(d) => <span>{DIE[d - 1]}</span>}</For>
                </div>
              )}
            </Show>
            <div class="dora" aria-label={t.dora}>
              <span class="dora-label">{t.dora}</span>
              {/* The indicator as a table shows it, then the dora it points to, a plain copy: a dora alone tripped everyone who adds one by reflex, an indicator alone
                  everyone new to it, and a phone has no wall to read it on (reviews 2026-11-01). */}
              <For each={h().indicators}>
                {(id) => (
                  <span class="dora-pair" title={t.doraPair}>
                    <span class="dora-indicator">
                      <Tile id={id} red={props.v.redFives} size="s" />
                    </span>
                    <span class="dora-to" aria-hidden="true">
                      ›
                    </span>
                    <Tile id={doraOf(id) * 4 + 1} red={false} size="s" />
                  </span>
                )}
              </For>
            </div>
          </div>
        </div>
      )}
    </Show>
  );
}

/** An element's place in the page as laid out, ignoring transforms (the board's surface leans). */
function laidOut(el: HTMLElement): { x: number; y: number } {
  let x = 0;
  let y = 0;
  for (let e: HTMLElement | null = el; e; e = e.offsetParent as HTMLElement | null) {
    x += e.offsetLeft;
    y += e.offsetTop;
  }
  return { x, y };
}

/** Reads the places the page laid out, in the board surface's pixels (the tile layer's frame). */
function measure(board: HTMLElement, refs: ZoneRefs, ownHand: HTMLElement | null): Geometry | null {
  const bp = laidOut(board);
  const b = { left: bp.x, top: bp.y, width: board.offsetWidth, height: board.offsetHeight };
  if (!b.width) return null;
  // A tile's width where it lies, from the hidden probe tile in each place (its layout width: a turned
  // seat's bounding box would give its height).
  const probe = (el: Element | null) => (el?.querySelector(".probe") as HTMLElement | null)?.offsetWidth ?? 0;
  const seats = [0, 1, 2, 3].map((rel) => {
    const zone = refs.zone[rel];
    const river = refs.river[rel];
    if (!zone || !river) return null;
    // The zone is placed by its transform about its top-left corner (the board's centre).
    const m = new DOMMatrixReadOnly(getComputedStyle(zone).transform === "none" ? undefined : getComputedStyle(zone).transform);
    const frame = { ox: zone.offsetLeft + m.e, oy: zone.offsetTop + m.f, ux: m.a, uy: m.b, vx: m.c, vy: m.d };
    const rt = probe(river);
    const hand = refs.hand[rel];
    const wall = refs.wall[rel];
    return {
      frame,
      river: { u: river.offsetLeft, v: river.offsetTop, t: rt, gap: Math.max(1, 0.05 * rt) },
      hand: { u: hand?.offsetLeft ?? 0, v: hand?.offsetTop ?? 0, t: probe(hand ?? null) },
      wall: { v: wall?.offsetTop ?? 0, t: probe(wall ?? null) },
    };
  });
  if (seats.some((s) => !s)) return null;
  const op = ownHand ? laidOut(ownHand) : null;
  const o = ownHand && op ? { left: op.x, width: ownHand.offsetWidth, bottom: op.y + ownHand.offsetHeight } : null;
  const T = probe(ownHand);
  return {
    seats: seats as Geometry["seats"],
    center: { x: b.width / 2, y: b.height / 2 },
    own: {
      cx: o ? o.left - b.left + o.width / 2 : b.width / 2,
      bottom: o ? o.bottom - b.top : b.height,
      t: T,
      gap: 0.03 * T,
      drawnGap: 0.35 * T,
      meldT: 0.72 * T,
      meldGap: 0.4 * T,
    },
  };
}

export function Table(props: {
  v: View;
  view?: BoardView; // how the board is laid out (design 10 §6)
  angle?: number; // how far the board leans back from straight above, in degrees (design 10 T15); else as last set
  feel?: Feel; // how tiles move (design 10 §7)
  onLand?: (p: Placement) => void; // a tile has landed: a click, a pulse
  heldScores?: boolean; // a hand's result is still to be read: the scores and counters as they were before it
  untimed?: boolean; // the lessons: no countdown
  backdrop?: boolean; // the signed-out page's dimmed table behind its words: no weave on its felt (ui/felt)
  onAct?: (a: Action) => void;
  hand?: number[]; // the own concealed tiles in the order shown; the view's (sorted) by default
  onRearrange?: (tile: number, to: number) => void; // auto-sort off: a tile dragged sideways to a slot
  marks?: (tile: number) => TileMark | null; // a review's bars over the hand's tiles (design 12 §5)
  actions?: () => JSX.Element; // a review's other actions (calls, riichi, passing) where the call buttons stand
  info?: PlayerInfos; // the seats' avatars and flairs (design 14 §7)
  ratings?: boolean; // a ranked table: the ratings shown on the cards
  noAssists?: boolean; // a rated table's rules (design 14 §6.6): every button shown, nothing told by the table
  onPress?: (type: ActionType) => void; // such a table: a button with nothing behind it
  callsOpen?: boolean; // such a table: a discard's window shows
  offline?: boolean; // the connection is down: nothing can be played, and the hand says so
  keysOff?: () => boolean; // a dialog is open over the table: its keys are the dialog's
}) {
  const me = () => props.v.seats[props.v.seat]!;
  let root!: HTMLDivElement;
  let board!: HTMLDivElement;
  let surface!: HTMLDivElement;
  let ownRow!: HTMLDivElement;
  let ownHand!: HTMLDivElement;
  const refs: ZoneRefs = { zone: [], river: [], wall: [], hand: [] };
  const [geo, setGeo] = createSignal<Geometry | null>(null, { equals: (a, b) => JSON.stringify(a) === JSON.stringify(b) });

  // The places are measured when the page lays them out: at first, and whenever the board, a zone or the
  // hand's place changes size (a rotation, a resize, a label growing), or anything that moves the board
  // against the hand does: the own row growing (call buttons, the status) and the table's room changing (the
  // replay bar wrapping). The board is centred in what the own row leaves, so those move it without resizing
  // it, and the hand was left where it had been measured (audit 2026-10-14).
  const remeasure = () => setGeo(measure(surface, refs, ownHand));
  const [compass, setCompass] = createSignal(false);
  // The compass's words in a task of their own once their face is in: at once where it is cached (or there is no face
  // to wait for), after the tiles on a first visit (above). With the board's task, the signed-out page's table took
  // 50–60 ms in a third of the runs (design 11 §9).
  createEffect(() => {
    if (compass() || (!face && !tilesDrawn() && globalThis.document?.fonts)) return;
    let live = true;
    void loadFace().then(() => setTimeout(() => live && setCompass(true), 0));
    onCleanup(() => (live = false));
  });
  onMount(() => {
    if (!props.backdrop) weaveSoon();
    // In a task of its own: the board renders first, its tiles (placed once it is measured) in the next task; the
    // compass's words in one of theirs once their face is in (below).
    const first = setTimeout(remeasure, 0);
    onCleanup(() => clearTimeout(first));
    const ro = new ResizeObserver(remeasure);
    // The own row's parts too: on wide screens the row is no box of its own (display: contents), and the
    // buttons' or the status's growing moves the hand's grid row.
    for (const el of [root, board, surface, ownRow, ...ownRow.children, ...(ownRow.querySelector(".own-side")?.children ?? []), ...refs.zone]) if (el) ro.observe(el);
    onCleanup(() => ro.disconnect());
  });

  const prompt = () => props.v.prompt;
  const legal = () => prompt()?.prompt.legal ?? [];
  // A tile played by the keyboard goes, and the focus with it: at the seat's next turn it comes back to the hand, at the tile just drawn.
  let handHadFocus = false;
  onMount(() => {
    const focused = (e: FocusEvent) => {
      if (e.target instanceof Element && e.target.matches(".tile-hit:focus-visible")) handHadFocus = true;
      else if (e.target instanceof Element && !e.target.matches(".tile-hit")) handHadFocus = false;
    };
    const pressed = () => {
      handHadFocus = false;
    };
    root.addEventListener("focusin", focused);
    addEventListener("pointerdown", pressed);
    onCleanup(() => {
      root.removeEventListener("focusin", focused);
      removeEventListener("pointerdown", pressed);
    });
  });
  createEffect(
    on(
      () => prompt()?.prompt.promptId,
      () => {
        const p = prompt();
        if (!handHadFocus || !p || (p.prompt.kind !== PromptKind.AFTER_DRAW && p.prompt.kind !== PromptKind.AFTER_CLAIM)) return;
        if (document.activeElement && document.activeElement !== document.body) return;
        setTimeout(() => [...root.querySelectorAll<HTMLElement>(".tile-hit:not(:disabled)")].sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left).at(-1)?.focus(), 400);
      },
      { defer: true },
    ),
  );
  const locked = () => !prompt() || prompt()!.sent || !!props.offline;
  // Riichi pressed: nothing has gone to the server, the player chooses the tile, and can change their mind. Only then are the declaration and the
  // discard sent, one after the other (game/session).
  const [riichi, setRiichi] = createSignal<Action | null>(null);
  createEffect(on(createMemo(() => prompt()?.prompt.promptId), () => setRiichi(null)));
  const readyAfter = createMemo(() => {
    if (!riichi()) return null;
    const all = [...me().concealed, me().drawn].filter(isTile);
    return new Set(all.filter((id) => waits(all.filter((x) => x !== id)).length > 0));
  });
  const discardFor = (id: number) => {
    const ok = readyAfter();
    return ok && !ok.has(id) ? undefined : legal().find((a) => a.type === ActionType.DISCARD && a.tiles[0] === id);
  };
  const restricted = () => {
    const p = prompt();
    return !props.noAssists && p !== null && (!!riichi() || p.prompt.kind === PromptKind.RIICHI_DISCARD || p.prompt.kind === PromptKind.AFTER_CLAIM);
  };
  const calledTile = () => {
    const d = props.v.lastDiscard;
    return d ? props.v.seats[d.seat]?.river[d.index]?.tile : undefined;
  };
  const act = (a: Action) => {
    if (a.type !== ActionType.DECLARE_RIICHI || a.tiles.length > 0) return props.onAct?.(a);
    setRiichi(a);
    // Should nothing here find what the rules found: the server's own prompt still asks for the tile.
    if (readyAfter()?.size === 0) {
      setRiichi(null);
      props.onAct?.(a);
    }
  };
  createEffect(() => {
    if (!riichi()) return;
    const cancel = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setRiichi(null);
      }
    };
    addEventListener("keydown", cancel, true);
    onCleanup(() => removeEventListener("keydown", cancel, true));
  });
  // Without assists a call window shows nothing: no clock. The seat's own turn does, as over the table.
  const windowHidden = () => {
    const p = prompt();
    return !!props.noAssists && p !== null && (p.prompt.kind === PromptKind.WINDOW || p.prompt.kind === PromptKind.LAST_TILE_OFFER);
  };
  const shown = () => props.hand ?? me().concealed;

  // The lean the board shows: the tiles' pictures are drawn for it (ui/tileArt).
  const lean = () => {
    artVersion(); // a sheet shown may change it
    return tableAngle();
  };
  const placements = createMemo(() => {
    const g = geo();
    if (!g) return [];
    const r = restricted();
    const frozen = !!props.offline;
    return place(props.v, { tiles: shown(), dim: (id) => frozen || (r && !discardFor(id)) }, { ...g, lean: lean() });
  });
  const last = createMemo(() => {
    const d = props.v.lastDiscard;
    if (!d) return null;
    const river = placements().filter((x) => x.seat === d.seat && x.where === "river");
    return river[river.length - 1] ?? null;
  });

  // The slot a tile dropped at page x takes: how many of the other tiles' centres lie left of it.
  const dropAt = (id: number, x: number) => {
    const left = laidOut(surface).x - window.scrollX;
    const others = placements().filter((q) => q.seat === props.v.seat && q.where === "hand" && q.tile !== id);
    props.onRearrange?.(id, others.filter((q) => q.x + left < x).length);
  };
  const own = (q: Placement): OwnTile | null => {
    const m = me();
    if (q.seat !== props.v.seat || (q.where !== "hand" && q.where !== "drawn") || !m.known) return null;
    const d = discardFor(q.tile);
    const onAct = props.onAct;
    const declaring = riichi();
    return {
      onClick: !locked() && d && onAct ? () => (declaring ? (setRiichi(null), onAct(create(ActionSchema, { type: ActionType.DECLARE_RIICHI, tiles: [q.tile] }))) : onAct(d)) : undefined,
      onMove: props.onRearrange && q.tile !== m.drawn ? (x) => dropAt(q.tile, x) : undefined,
    };
  };

  const order = () => [0, 1, 2, 3].map((r) => (props.v.seat + r) % SEATS);
  // The board leans back about its near edge, seen without perspective (design 10 T15): its surface turns by
  // one transform, which the graphics chip applies, and the tiles are drawn for the same angle. Until the
  // tiles for a new angle are drawn, the board keeps the angle of those it shows.
  createEffect(() => {
    if (props.angle !== undefined) setAngle(props.angle);
  });
  // The sprites are drawn at the sizes the table shows (ui/tileArt).
  createEffect(() => {
    const g = geo();
    if (g?.own.t && g.seats[0]?.river.t) setSizes(g.own.t, g.seats[0].river.t);
  });
  // The places as measured: when they change (a resize, the fonts arriving), tiles jump to their new places;
  // only the game moves them in flight.
  const layoutKey = createMemo(() => (geo() ? JSON.stringify(geo()) : ""));
  // The board never shows without its tiles (design 11 §3): laid out hidden until its places are measured and
  // while a sheet remembered from an earlier visit is on its way, then shown with them in one frame; never held
  // back more than 150 ms for a sheet.
  const [waited, setWaited] = createSignal(false);
  const [tilesMade, setTilesMade] = createSignal(false);
  const visible = createMemo(() => {
    artVersion();
    return !!geo() && tilesMade() && (waited() || sheetsSettled());
  });
  createEffect(() => {
    if (visible()) return;
    const id = setTimeout(() => setWaited(true), 150);
    onCleanup(() => clearTimeout(id));
  });
  createEffect(
    on(visible, (v) => {
      if (!v) return;
      setWaited(true); // once shown, it stays
      milestone("mj:board"); // the board's first frame (design 11 §3)
    }),
  );
  // The page sizes the board for it (styles.css, --fit), and the hand's place follows the lean on wide screens
  // (it overlaps the mat less as the board leans): measured again once laid out.
  createEffect(() => {
    document.documentElement.style.setProperty("--lean", `${lean()}deg`);
    const id = setTimeout(remeasure, 0);
    onCleanup(() => clearTimeout(id));
  });
  // Where a seat's callout stands: over its side of the wall, nine tile widths out from the centre along the
  // seat's direction (its river ends nearer, its hand further), projected by the camera as a tile is.
  const calloutAt = (rel: number) => {
    const g = geo();
    const s = g?.seats[rel];
    if (!g || !s) return null;
    const len = Math.hypot(s.frame.vx, s.frame.vy) || 1;
    const d = 9 * s.river.t;
    const y = g.center.y + (s.frame.vy / len) * d;
    const bottom = g.center.y * 2;
    return { x: g.center.x + (s.frame.vx / len) * d, y: bottom - (bottom - y) * Math.cos((lean() * Math.PI) / 180), ux: Math.sign(Math.round(s.frame.vx / len)), uy: Math.sign(Math.round(s.frame.vy / len)) };
  };
  // What a callout must not cover: every river's and every call's tiles and your own hand, as the camera shows them (a tile's long
  // side both ways, whatever its turn).
  const calloutAvoids = (): Box[] => {
    const g = geo();
    if (!g) return [];
    const bottom = g.center.y * 2;
    const c = Math.cos((lean() * Math.PI) / 180);
    return placements()
      .filter((q) => q.where === "river" || q.where === "meld" || (q.seat === props.v.seat && (q.where === "hand" || q.where === "drawn")))
      .map((q) => {
        const y = q.screen ? q.y : bottom - (bottom - q.y) * c;
        const half = (q.width * H) / 2;
        return { l: q.x - half, t: y - half, r: q.x + half, b: y + half };
      });
  };
  // Where a seat's card stands (design 14 §7): the four corners between the rivers, each seat's on its left hand
  // going round the table, projected by the camera as a tile is; the card itself stays upright.
  const cardAt = (rel: number) => {
    const g = geo();
    const s = g?.seats[rel];
    if (!g || !s) return null;
    const a = 3.35 * s.river.t;
    const sx = rel === 0 || rel === 3 ? -1 : 1;
    const sy = rel === 0 || rel === 1 ? 1 : -1;
    const bottom = g.center.y * 2;
    const y = g.center.y + sy * a;
    return { x: g.center.x + sx * a, y: bottom - (bottom - y) * Math.cos((lean() * Math.PI) / 180), sx, sy, avatar: Math.max(18, Math.round(s.river.t * 1.15)) };
  };
  return (
    <div class="table" ref={root}>
      <SeatsInWords v={props.v} />
      <div class={`board view-${boardView(props.view)}${visible() ? "" : " waiting"}`} ref={board}>
        <div class="surface" ref={surface} style={lean() ? { transform: `rotateX(${lean()}deg)` } : undefined}>
          <Show when={compass()} fallback={<div class="center" />}>
            <Center v={props.v} held={props.heldScores} />
          </Show>
          <For each={order()}>{(seat) => <Zone v={props.v} seat={seat} refs={refs} />}</For>
        </div>
        {/* Under the tiles: a long river or a riichi's sideways tile runs up to a card, and a discard hidden is safety information hidden. */}
        <div class="callout-layer card-layer">
          <For each={order()}>{(seat) => <PlayerCard v={props.v} seat={seat} at={cardAt} info={props.info} ratings={props.ratings} held={props.heldScores} />}</For>
        </div>
        <Show when={geo()}>
          {(g) => (
            <TileLayer
              placements={placements()}
              red={props.v.redFives}
              base={g().own.t || 40}
              fps={FPS}
              last={last()}
              own={own}
              dealing={dealing(props.v)}
              mark={props.marks}
              hand={`${props.v.gameId}:${props.v.hand?.index ?? -1}`}
              feel={effectiveFeel(props.feel)}
              onLand={props.onLand}
              angle={lean()}
              bottom={g().center.y * 2}
              layout={layoutKey()}
              onReady={() => setTilesMade(true)}
            />
          )}
        </Show>
        <div class="callout-layer">
          <Callout v={props.v} at={calloutAt} avoid={calloutAvoids} />
        </div>
      </div>
      <div class="own" ref={ownRow}>
        <div class="own-side">
        <div class="own-status">
          {/* A new prompt (its receivedAt) is a new countdown. */}
          <Show when={prompt() && !prompt()!.sent && !props.untimed && !props.v.untimed && !windowHidden() ? prompt() : null} keyed>
            {(p) => <OwnClock parts={(now) => promptClock(p.prompt, p.receivedAt, now)} />}
          </Show>
          <Show when={prompt()?.rejected}>
            <span class="notice">{t.illegal}</span>
          </Show>
          <Show when={props.v.aids?.furiten}>
            <span class="tag warn">{t.furiten}</span>
          </Show>
          <Show when={props.v.aids && props.v.aids.waits.length > 0}>
            <span class="waits" aria-label={t.waits}>
              {t.waits}
              <For each={props.v.aids!.waits}>
                {(k) => (
                  <span class="wait">
                    <Tile id={k * 4} red={false} size="s" />
                    <small>×{unseen(props.v, k)}</small>
                  </span>
                )}
              </For>
            </span>
          </Show>
        </div>
        <Show when={props.onAct} fallback={props.actions?.()}>
          <Show
            when={!riichi()}
            fallback={
              <div class="controls riichi-choice" role="group" aria-label={t.actions.DECLARE_RIICHI}>
                <span class="riichi-choice-text">{t.riichiChoose}</span>
                <button type="button" class="secondary" onClick={() => setRiichi(null)}>
                  {t.cancel}
                </button>
              </div>
            }
          >
            {props.noAssists ? (
              <OpenControls
                legal={legal()}
                red={props.v.redFives}
                locked={locked()}
                ownTurn={prompt()?.prompt.kind === PromptKind.AFTER_DRAW}
                callsOpen={props.callsOpen}
                called={calledTile()}
                onAct={act}
                onPress={(type) => props.onPress?.(type)}
                keysOff={props.keysOff}
              />
            ) : (
              <Controls legal={legal()} red={props.v.redFives} locked={locked()} called={calledTile()} onAct={act} keysOff={props.keysOff} />
            )}
          </Show>
        </Show>
        </div>
        {/* The own hand's place: its tiles are drawn by the tile layer. */}
        <div class={`own-hand${props.v.players[props.v.seat]?.drawnOnTop ? " on-top" : ""}`} ref={ownHand}>
          <span class="probe tile tile-l" />
        </div>
      </div>
    </div>
  );
}
